// ==========================================================
// FILE: src/app/core/localdata/core-sqlite-services/productos/product-local.service.ts
// ==========================================================
import { Injectable } from '@angular/core';
import { SqliteConexionService } from '../sqlite-conexion.service';
import { PendingCoreService } from '../../pending/pending-core.service';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { DeviceCoreService } from '../../../system/device-core.service';

export interface ProductLocal {
  rowid?: number;
  backend_id?: number; // ID del producto en backend (cuando sincronizado)
  ref: string;
  label: string;
  description?: string;
  note_public?: string;
  note?: string;
  price?: number;
  price_base_type?: string;
  default_vat_code?: string;
  tva_tx?: number;
  tosell?: number;
  tobuy?: number;
  fk_product_type?: number;
  url?: string;
  barcode?: string;
  stock?: number;
  finished?: number;
  desiredstock?: number;
  weight?: number;
  weight_units?: string;
  expiry_date?: string;
  datec?: string;
  tms?: string;
  synced?: number;
  userId?: string;
  deviceToken?: string;
}

@Injectable({ providedIn: 'root' })
export class ProductLocalService {
  private table = 'llx_product';

  constructor(
    private sqliteService: SqliteConexionService,
    private pendingService: PendingCoreService,
    private authService: AuthService,
    private deviceService: DeviceCoreService
  ) {}

  private async getUserAndDevice(): Promise<{ userId: string; deviceToken: string }> {
    const device = await this.deviceService.getDeviceInfo();
    const user = await this.authService.getCurrentUserAsync();
    if (!user) throw new Error('No hay usuario logueado para PendingOps');
    return { userId: user.login, deviceToken: device.deviceToken };
  }

  async getAllProducts(): Promise<ProductLocal[]> {
    return await this.sqliteService.query(`SELECT * FROM ${this.table} ORDER BY rowid`);
  }

  async getProductByRef(ref: string): Promise<ProductLocal | undefined> {
    const res = await this.sqliteService.query(`SELECT * FROM ${this.table} WHERE ref = ?`, [ref]);
    return res[0];
  }

  async getProductByRowId(rowid: number): Promise<ProductLocal | undefined> {
    const res = await this.sqliteService.query(`SELECT * FROM ${this.table} WHERE rowid = ?`, [rowid]);
    return res[0];
  }

  async addProduct(product: ProductLocal): Promise<number> {
    product.synced = 0;
    const { userId, deviceToken } = await this.getUserAndDevice();
    product.userId = userId;
    product.deviceToken = deviceToken;

    const insertable = { ...product };
    delete insertable.rowid;

    const fields = Object.keys(insertable).join(', ');
    const placeholders = Object.keys(insertable).map(() => '?').join(', ');
    const values = Object.values(insertable);

    await this.sqliteService.execute(`INSERT INTO ${this.table} (${fields}) VALUES (${placeholders})`, values);

    const res = await this.sqliteService.query('SELECT last_insert_rowid() AS id');
    product.rowid = res[0].id;

    await this.pendingService.addOp({ table: this.table, action: 'INSERT', payload: product });
    return product.rowid!;
  }

  async updateProduct(product: ProductLocal): Promise<void> {
    if (!product.rowid) throw new Error('rowid es requerido');
    const { userId, deviceToken } = await this.getUserAndDevice();
    product.userId = userId;
    product.deviceToken = deviceToken;

    const updates = Object.entries(product)
      .filter(([key]) => key !== 'rowid')
      .map(([key]) => `${key} = ?`)
      .join(', ');

    const values = Object.entries(product)
      .filter(([key]) => key !== 'rowid')
      .map(([_, value]) => value);

    await this.sqliteService.execute(`UPDATE ${this.table} SET ${updates} WHERE rowid = ?`, [...values, product.rowid]);
    await this.pendingService.addOp({ table: this.table, action: 'UPDATE', payload: product });
  }

  async deleteProduct(rowid: number): Promise<void> {
    const { userId, deviceToken } = await this.getUserAndDevice();
    await this.sqliteService.execute(`DELETE FROM ${this.table} WHERE rowid = ?`, [rowid]);
    await this.pendingService.addOp({ table: this.table, action: 'DELETE', payload: { rowid, userId, deviceToken }, deleted: 1 });
  }

  async updateBackendId(localId: number, backendId: number): Promise<void> {
    await this.sqliteService.execute(`UPDATE ${this.table} SET backend_id = ?, synced = 1 WHERE rowid = ?`, [backendId, localId]);
  }
}
